const DEVELOPER_ASSISTANCE_BROWSER_ARTIFACT = 'paypercut-developer-assistance.iife.min.js';
const DEVELOPER_ASSISTANCE_MODULE_ARTIFACT = 'developer-assistance.mjs';
const DEVELOPER_ASSISTANCE_LOAD_DIAGNOSTIC =
  '[Paypercut Elements] developer_assistance_load_failed: Developer Assistant is unavailable. The SDK and Elements continue to work.';
const IIFE_ARTIFACT_PATH = /\/paypercut-checkout(?:-dev)?\.iife\.min\.js$/;

declare const __filename: string | undefined;

// Rollup rewrites import.meta.url for CommonJS and classic-script bundles, so
// this remains the URL of the exact core artifact that owns the registration
// boundary in every supported format.
const coreArtifactUrl = typeof __filename === 'string' ? fileUrlFor(__filename) : import.meta.url;
const classicScriptUrl =
  typeof document === 'undefined' ? null : (document.currentScript?.getAttribute('src') ?? null);

let loadRequested = false;

export function requestDeveloperAssistance(isInstalled: () => boolean): void {
  if (loadRequested || isInstalled()) return;
  loadRequested = true;
  if (!canLoadDeveloperAssistance()) {
    warnDeveloperAssistanceUnavailable();
    return;
  }

  void Promise.resolve()
    .then(loadDeveloperAssistance)
    .then(() => {
      if (!isInstalled()) throw new Error();
    })
    .catch(warnDeveloperAssistanceUnavailable);
}

function warnDeveloperAssistanceUnavailable(): void {
  console.warn(DEVELOPER_ASSISTANCE_LOAD_DIAGNOSTIC);
}

function canLoadDeveloperAssistance(): boolean {
  return (
    typeof document !== 'undefined' &&
    typeof document.createElement === 'function' &&
    Boolean(document.head || document.documentElement)
  );
}

function loadDeveloperAssistance(): Promise<unknown> {
  if (classicScriptUrl || isClassicScriptArtifact(coreArtifactUrl)) {
    return loadClassicScript(resolveBrowserArtifactUrl(coreArtifactUrl));
  }

  const moduleUrl = new URL(DEVELOPER_ASSISTANCE_MODULE_ARTIFACT, coreArtifactUrl).href;
  return import(/* @vite-ignore */ moduleUrl);
}

function fileUrlFor(filename: string): string {
  const normalized = filename.replace(/\\/g, '/');
  return new URL(`file://${normalized.startsWith('/') ? '' : '/'}${normalized}`).href;
}

function resolveBrowserArtifactUrl(coreUrl: string): string {
  const url = new URL(coreUrl);
  if (IIFE_ARTIFACT_PATH.test(url.pathname)) {
    const artifactUrl = new URL(DEVELOPER_ASSISTANCE_BROWSER_ARTIFACT, url);
    artifactUrl.search = url.search;
    artifactUrl.hash = '';
    return artifactUrl.href;
  }
  if (/\.js$/.test(url.pathname)) {
    url.pathname = url.pathname.replace(/[^/]+$/, DEVELOPER_ASSISTANCE_BROWSER_ARTIFACT);
  } else if (/\/cdn\/?$/.test(url.pathname)) {
    url.pathname = url.pathname.replace(
      /\/cdn\/?$/,
      `/dist/${DEVELOPER_ASSISTANCE_BROWSER_ARTIFACT}`,
    );
  } else {
    url.pathname = `${url.pathname.replace(/\/$/, '')}/dist/${DEVELOPER_ASSISTANCE_BROWSER_ARTIFACT}`;
  }
  url.hash = '';
  return url.href;
}

function isClassicScriptArtifact(url: string): boolean {
  try {
    return IIFE_ARTIFACT_PATH.test(new URL(url).pathname);
  } catch {
    return false;
  }
}

function loadClassicScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error()), { once: true });
    (document.head || document.documentElement).appendChild(script);
  });
}
